import { Polygon } from "geojson";
import { LngLat } from "mapbox-gl";
import { useMemo } from "react";
import { useMap } from "react-map-gl";
import { useDrawMapStore, useMapMarkerStore, usePipedriveStore, usePropertyStore } from "..";
import { showErrorToast, showSuccessToast } from "../components/ToastContainer";
import { IDealState } from "../interfaces/IDeal";
import GeometryService from "../services/geometryService";
import useCurrentOrFallbackPipedriveUserId from "./useCurrentOrFallbackPipedriveUserId";

const useDealCreate = (): (dealState: IDealState, satelliteScreenshot?: File | null) => Promise<number> => {
    const map = useMap().map!;
    const pipedriveStore = usePipedriveStore();
    const drawMapStore = useDrawMapStore();
    const mapMarkerStore = useMapMarkerStore();
    const propertyStore = usePropertyStore();
    const geometryService = useMemo(() => new GeometryService(), []);
    const pipedriveUserId = useCurrentOrFallbackPipedriveUserId();

    return async (dealState: IDealState, satelliteScreenshot?: File | null): Promise<number> => {
        try {
            const { latitude, longitude } = mapMarkerStore.coordinates!;
            const coordinate = new LngLat(longitude, latitude);
            await propertyStore.fetchProperty(coordinate, map);
            const regridId = propertyStore.getProperty(coordinate)?.regridId ?? dealState.regridId;

            const createdDealId = await pipedriveStore.createDeal(
                { ...dealState, regridId: regridId },
                await pipedriveStore.getOrCreatePipedrivePersonId(dealState.contactPersonName),
                await pipedriveStore.getOrCreatePipedriveOrganizationId(dealState.contactCompanyName),
                await pipedriveStore.getOrCreatePipedrivePersonId(dealState.brokerName),
                await pipedriveStore.getOrCreatePipedrivePersonId(dealState.propertyOwnerName),
                latitude,
                longitude,
                dealState.dealShapeApns!.map((apn, index) => ({
                    apn: apn,
                    points: geometryService.mapPolygonToShape(drawMapStore.result!.features[index].geometry as Polygon).points,
                })),
            );

            if (satelliteScreenshot) {
                await pipedriveStore.createDealFile(satelliteScreenshot, satelliteScreenshot.name, createdDealId);
            }

            for (const attachment of dealState.attachments!) {
                await pipedriveStore.createDealFile(attachment, attachment.name, createdDealId);
            }

            if (dealState.notes)
                await pipedriveStore.createDealNote(createdDealId, dealState.notes, pipedriveUserId);

            if (dealState.lostComment?.trim())
                await pipedriveStore.createDealNote(createdDealId, dealState.lostComment, pipedriveUserId);

            showSuccessToast();

            return createdDealId;
        } catch (e) {
            showErrorToast();
            throw e;
        }
    };
};

export default useDealCreate;